import React, { useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Camera, Key, Package } from 'lucide-react';

const RentalEquipmentFormDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  onSubmit,
  onPhotoUpload,
  uploadingPhoto
}) => {
  const fileInputRef = useRef(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Nouvel Équipement de Location</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Photo</Label>
            <div className="flex items-center gap-4">
              {formData.photo ? (
                <img
                  src={formData.photo}
                  alt="Aperçu"
                  className="w-20 h-20 object-cover rounded-lg"
                />
              ) : (
                <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center">
                  <Package className="w-8 h-8 text-muted-foreground" />
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onPhotoUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="rounded-full"
              >
                <Camera className="w-4 h-4 mr-2" />
                {uploadingPhoto ? 'Téléchargement...' : 'Ajouter une photo'}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rental_name">Nom *</Label>
              <Input
                id="rental_name"
                data-testid="rental-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rental_type">Type</Label>
              <Input
                id="rental_type"
                data-testid="rental-type-input"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rental_article">Numéro d'article</Label>
              <Input
                id="rental_article"
                data-testid="rental-article-input"
                value={formData.article_number}
                onChange={(e) => setFormData({ ...formData, article_number: e.target.value })}
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rental_order">Numéro de commande</Label>
              <Input
                id="rental_order"
                data-testid="rental-order-input"
                value={formData.order_number}
                onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                className="rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rental_quantity">Quantité *</Label>
              <Input
                id="rental_quantity"
                data-testid="rental-quantity-total-input"
                type="number"
                min="1"
                value={formData.quantity_total}
                onChange={(e) => setFormData({ ...formData, quantity_total: parseInt(e.target.value) || 1 })}
                required
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rental_supplier">Fournisseur</Label>
              <Input
                id="rental_supplier"
                data-testid="rental-supplier-input"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                className="rounded-lg"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rental_description">Description</Label>
            <Textarea
              id="rental_description"
              data-testid="rental-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description détaillée de l'équipement..."
              className="rounded-lg"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="rental_expiration">Date d'expiration</Label>
            <Input
              id="rental_expiration"
              data-testid="rental-expiration-input"
              type="date"
              value={formData.expiration_date}
              onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
              className="rounded-lg"
            />
            <p className="text-xs text-muted-foreground">
              Optionnel - Date de fin de garantie, certification, etc.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="storage_location">Emplacement de stockage / retour</Label>
            <Input
              id="storage_location"
              data-testid="rental-location-input"
              value={formData.storage_location}
              onChange={(e) => setFormData({ ...formData, storage_location: e.target.value })}
              placeholder="Ex: Atelier A, Armoire 3..."
              className="rounded-lg"
            />
            <p className="text-xs text-muted-foreground">
              Où l'équipement doit être rangé après utilisation
            </p>
          </div>

          <Button
            data-testid="rental-submit-button"
            type="submit"
            className="w-full rounded-full"
          >
            <Key className="w-4 h-4 mr-2" />
            Ajouter à la Location
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RentalEquipmentFormDialog;
